import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { LayoutDashboard, Package, ClipboardList, Users, LogOut, ShoppingBag, MessageSquare, Activity, BarChart3, Image as ImageIcon, Settings as SettingsIcon } from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';
import { toast } from 'sonner';

// Module-level 401 interceptor — registers once on first import so it's always
// active before any admin page fires its requests. Without this, child effects
// (e.g. AdminProducts.fetchProducts) run BEFORE parent effects in React, causing
// the empty "No products found" screen that looks like data was deleted.
let interceptorRegistered = false;
const registerAuthInterceptor = () => {
  if (interceptorRegistered) return;
  interceptorRegistered = true;
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        // Only act on admin routes; ignore 401s from public endpoints
        if (
          window.location.pathname.startsWith('/admin') &&
          !window.location.pathname.startsWith('/admin/login')
        ) {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user');
          toast.error('Your session has expired. Please sign in again.', {
            duration: 5000,
          });
          window.location.replace('/admin/login');
        }
      }
      return Promise.reject(error);
    }
  );
};
registerAuthInterceptor();

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [adminUser, setAdminUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    const user = localStorage.getItem('admin_user');

    if (!token) {
      navigate('/admin/login');
      return;
    }

    if (user) {
      setAdminUser(JSON.parse(user));
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin/login');
  };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
    { icon: BarChart3, label: 'Analytics', path: '/admin/analytics' },
    { icon: Package, label: 'Products', path: '/admin/products' },
    { icon: ShoppingBag, label: 'Orders', path: '/admin/orders' },
    { icon: ClipboardList, label: 'Custom Orders', path: '/admin/custom-orders' },
    { icon: MessageSquare, label: 'Reviews', path: '/admin/reviews' },
    { icon: ImageIcon, label: 'Media', path: '/admin/media' },
    { icon: Users, label: 'Admin Users', path: '/admin/users' },
    { icon: Activity, label: 'Audit Log', path: '/admin/audit-log' },
    { icon: SettingsIcon, label: 'Settings', path: '/admin/settings' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="flex h-screen bg-gray-100">
      <aside className="w-64 bg-gray-900 text-white flex flex-col">
        <div className="p-6 border-b border-gray-800">
          <h1 className="text-2xl font-bold">Admin Panel</h1>
        </div>

        <nav className="flex-1 p-4 space-y-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive(item.path)
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-300 hover:bg-gray-800'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="p-4 border-t border-gray-800">
          <div className="mb-4">
            <p className="text-xs text-gray-400 mb-1">Signed in as:</p>
            <p className="text-sm font-medium truncate">{adminUser?.email}</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full border-gray-700 hover:bg-gray-800 text-white"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
